import {useState} from "react";
import {Alert, Radio} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {bffApi} from "../../chat/bffApi";
import {
    buildChecklistExportUrl,
    downloadExport,
    type Orientation,
} from "../checklist/exportHelpers";
import {ChecklistModalActions} from "../checklist/ChecklistModalActions";
import type {CollectionMeta, ModalBusyProps, TopicMeta} from "./types";
import type {Status} from "../types";
import classes from "../checklist/modalStyles.module.css";
import {newMetricEvent} from "../../chat/metrics";

type Format = "docx" | "xlsx";

type Props = {
    onClose: () => void;
    topicMeta: TopicMeta;
    collectionMeta?: CollectionMeta;
    labels?: string[];
    status?: (string | Status)[];
} & ModalBusyProps;

const rowClassNames = {
    root: classes.modalRow,
    labelWrapper: classes.modalRowLabelWrapper,
    label: classes.modalRowLabel,
};

export const GenerateChecklistTab = ({
    onClose,
    topicMeta,
    collectionMeta,
    labels = [],
    status,
    busy,
    onBusyChange,
}: Props) => {
    const {t} = useTranslation();
    const {data: user} = bffApi.endpoints.getUser.useQuery();
    const isdemo = !!user?.demokonto;

    const [format, setFormat] = useState<Format>("xlsx");
    const [orientation, setOrientation] = useState<Orientation>("landscape");
    const [trackInteraction] = bffApi.endpoints.trackInteraction.useMutation();

    const version = new URLSearchParams(window.location.search).get("version");

    const handleGenerate = async () => {
        if (isdemo) return;
        const operationId = crypto.randomUUID();
        onBusyChange(true);
        void trackInteraction({
            ...newMetricEvent(),
            action: "export_requested",
            operationId,
            format,
            contentType: "checklist",
            delivery: "download",
        });
        try {
            const url = buildChecklistExportUrl({
                format,
                topicId: topicMeta.topicId,
                collectionId: collectionMeta?.id,
                orientation,
                vlabels: labels,
                status,
                version: version ?? undefined,
                template: "0",
            });
            await downloadExport(url);
            void trackInteraction({
                ...newMetricEvent(),
                action: "export_completed",
                operationId,
                format,
                contentType: "checklist",
                delivery: "download",
            });
            // downloadExport has received the response and invoked a.click().
            void trackInteraction({
                ...newMetricEvent(),
                action: "file_download_started",
                operationId,
                subjectId: operationId,
                source: "checklist",
                format,
            });
            onClose();
        } catch {
            void trackInteraction({
                ...newMetricEvent(),
                action: "export_failed",
                operationId,
                format,
                contentType: "checklist",
                delivery: "download",
                reason: "unknown",
            });
            onBusyChange(false);
        }
    };

    return (
        <>
            <div className={classes.modalBody}>
                {isdemo && (
                    <Alert variant="light" color="gray">
                        {t("Info.GenerationNotAvailableForDemoUsers")}
                    </Alert>
                )}
                <div className={classes.modalSection}>
                    <div className={classes.subheader}>
                        {t("Word.DocumentType")}
                    </div>
                    <Radio.Group
                        value={format}
                        onChange={(v) => setFormat(v as Format)}
                    >
                        <div className={classes.modalRowList}>
                            <Radio
                                value="docx"
                                label="Word"
                                color="gneis.9"
                                classNames={rowClassNames}
                            />
                            <Radio
                                value="xlsx"
                                label="Excel"
                                color="gneis.9"
                                classNames={rowClassNames}
                            />
                        </div>
                    </Radio.Group>
                </div>
                <div className={classes.modalSection}>
                    <div className={classes.subheader}>
                        {t("Word.PaperOrientation")}
                    </div>
                    <Radio.Group
                        value={orientation}
                        onChange={(v) => setOrientation(v as Orientation)}
                    >
                        <div className={classes.modalRowList}>
                            <Radio
                                value="portrait"
                                label={t("Word.Portrait")}
                                color="gneis.9"
                                classNames={rowClassNames}
                            />
                            <Radio
                                value="landscape"
                                label={t("Word.Landscape")}
                                color="gneis.9"
                                classNames={rowClassNames}
                            />
                        </div>
                    </Radio.Group>
                </div>
            </div>
            <ChecklistModalActions
                primary={{
                    label: t("Action.GenerateChecklist"),
                    onClick: handleGenerate,
                    loading: busy,
                    disabled: isdemo,
                }}
                secondary={{
                    label: t("Word.Abort"),
                    onClick: onClose,
                    disabled: busy,
                }}
            />
        </>
    );
};
